<script setup>

import { ref, watchEffect, onMounted ,nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { formatCurrency } from '@/utils/currency';
import { formatCloseMonthShort } from '@/utils/closeMonth';
import axiosInstance from '@/api/axios';




const route = useRoute();

const expense = ref({
  document_number: '',
  created_by_name: '',
  close_month: '',
  created_at: '',
  currency: 'KRW',
  amount: 0,
  amount_tax: 0,
  amount_foreign: 0,
  fx_rate: 0,
  payment_due_date: '',
  partner_company_name: '',
  payment_method_name: '',
  account_number: '',
  account_holder: '',
  bank: '',
  our_compay_name: '',
  category_name: '',
  description: '',
  details: []
});
const loaded = ref(false)

const loadExpense = async (id) => {
  try {
    const response = await axiosInstance.get(`/expenses/${id}/pdf/`);
    expense.value = response.data;
    await nextTick();

    loaded.value = true
  } catch (error) {
    console.error('Error loading expense:', error);
  }
};

onMounted(async () => {
  const id = route.params.id;
  loadExpense(id);
  await nextTick(); // DOM 안정화 유도
});

const pageSize = 20;
const pagedDetails = ref(
    []);

watchEffect(() => {
    const chunks = [];
    const details = expense.value.details || [];

    for (let i = 0; i < details.length; i += pageSize) {
        chunks.push(details.slice(i, i + pageSize));
    }
    pagedDetails.value = chunks;
});



</script>

<template>
    <div id="expense-print">
  
        <!-- 페이지 1: 기본정보, 결재이미지, 비고 -->
        <div class="page page-1">

            <!-- 결재/집행 라인 -->
            <div class="approval-section mb-4">
                <h3 class="text-center">지출결의서</h3>
                <table class="table table-bordered text-center mb-2">
                    <tbody class="table-light">
                    <tr>
                        <th rowspan="2">결재</th>
                        <th>담당</th>
                        <th>대리</th>
                        <th>팀장</th>
                        <th>실장</th>
                        <th>이사</th>
                        <th>대표이사</th>
                    </tr>        
                    <tr>
                        <td style="height: 40px;"></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                    </tr>
                    </tbody>
                </table>
                <table class="table table-bordered text-center">
                    <tbody class="table-light">
                    <tr>
                        <th rowspan="2">집행</th>
                        <th>담당</th>
                        <th>과장</th>
                        <th>부장</th>
                        <th>이사</th>
                    </tr>        
                    <tr>
                        <td style="height: 40px;"></td>
                        <td></td>
                        <td></td>
                        <td></td>
                    </tr>
                    </tbody>
                </table>
            </div>


            <table class="table table-bordered">
            <tbody>
                <tr>
                <th class="table-light">지결번호</th>
                <td>{{ expense.document_number }}</td>
                <th class="table-light">금액</th>
                <td>{{ formatCurrency(expense.amount, 'KRW') }}</td>
                </tr>
                <tr>
                <th class="table-light">작성자</th>
                <td>{{ expense.created_by_name }}</td>
                <th class="table-light">마감월</th>
                <td>{{ formatCloseMonthShort(expense.close_month) }}</td>
                </tr>
                <tr>
                <th class="table-light">작성일자</th>
                <td>{{ expense.created_at }}</td>
                <th class="table-light">적용환율</th>
                <td>
                    <template v-if="expense.currency !== 'KRW'">
                    {{ formatCurrency(expense.amount_foreign, expense.currency) }} * {{ formatCurrency(expense.fx_rate, 'KRW') }}
                    </template>
                    <template v-else>-</template>
                </td>
                </tr>
                <tr>
                <th class="table-light">지급요청일</th>
                <td>{{ expense.payment_due_date }}</td>
                <th class="table-light">거래처</th>
                <td>{{ expense.partner_company_name }}</td>
                </tr>
                <tr>
                <th class="table-light">결제조건</th>
                <td>{{ expense.payment_method_name }}</td>
                <th class="table-light">입금계좌</th>
                <td>{{ expense.bank }} / {{ expense.account_number }} / {{ expense.account_holder }}</td>
                </tr>
                <tr>
                <th class="table-light">우리회사</th>
                <td>{{ expense.our_compay_name }}</td>
                <th class="table-light">비용구분</th>
                <td>{{ expense.category_name }}</td>
                </tr>
            </tbody>
            </table>

            <!-- 비고 -->
            <div class="mt-3">
                <h5 class="fw-bold">비고</h5>
                <div class="border p-2" style="height: 100px; white-space: pre-wrap;">
                    {{ expense.description || '-' }}
                </div>
            </div>

            <div class="page-footer">
                    Page {{ 1  }} / {{ pagedDetails.length + 1 }}
            </div>
        </div>

        <!-- 페이지 2+ : 세부내역 테이블을 Vue에서 나눔 -->
        <div v-show="loaded " v-for="(page, pIndex) in pagedDetails" :key="pIndex" class="page"   :class="{ 'page-2': pIndex === 0 }">
            <h5 class="fw-bold">세부내역 - Page {{ pIndex + 1 }}</h5>
            <table class="table table-bordered">
                <thead class="table-light">
                <tr>
                    <th>스타일번호</th>
                    <th>품명</th>
                    <th>규격</th>
                    <th>수량</th>
                    <th>단가</th>
                    <th>금액</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(item, index) in page" :key="index">
                    <td>{{ item.style_number }}</td>
                    <td>{{ item.material_name }}</td>
                    <td>{{ item.specification }}</td>
                    <td class="text-end">{{ item.quantity }}</td>
                    <td class="text-end">{{ formatCurrency(Number(item.unit_price), 'KRW') }}</td>
                    <td class="text-end">{{ formatCurrency(Number(item.amount), 'KRW') }}</td>
                </tr>
                </tbody>
            </table>
            <div class="page-footer">
                    Page {{ pIndex + 2 }} / {{ pagedDetails.length + 1 }}
            </div>
        </div>
    </div>
  </template>
  
  
  <style scoped>
    .page {
        width: 210mm;
        padding: 20mm;
        margin-bottom: 20mm;
        background: white;
        color: black;
        page-break-after: always;
        overflow: visible; /* ✅ 핵심 추가 */
        display: block;
        contain: layout paint; /* ✅ 크기 추적 최소화 */   
    }
    .page.page-1 {
        /* 예: 첫 페이지만 다르게 표시하고 싶을 때 */
        background: #f9f9f9;
        border-bottom: 2px solid black;
    }
    .page.page-2 {
        page-break-before: always;
        min-height: 300px;
    }

</style>
